import {
  AssetCondition,
  AssetStatus,
  AssetUpdateRequest,
  AssetActionEnum,
  AssetsMdmStatus,
  ProductTypeName,
  OSType,
  LockStatus,
  FilevaultStatus,
  BitlockerStatus,
  BatteryHealthStatus,
  ActionType,
  AssetEventCategory,
  SortOrder,
  AssetEventSource,
  WarrantyStatus,
  HealthStatus,
  OSEOLStatus,
  CurrencyCodeEnum,
  RelatedProductCategoryNameEnum,
} from '@electricjs/core_entity-client';

import { type Address } from '@electricjs/arc';

import {
  AssetConditionOption,
  AssetStatusOption,
  SiteOption,
  ProductTypeOption,
} from '@/components/Assets/helpers/transformOptions';

import { NON_PHYSICAL_PRODUCT_TYPES } from '@/constants/devices';

import { PaginationParams } from './common';
import { Product, ProductType } from './products';
import { DeviceIssueType } from './devices';
import { EOLStatus } from '@/types/devices';

export type AssetBase = {
  id: string;
  name: string;
  productType: ProductType;
};

// The camelCase version of the Asset from Core Entity
export type Asset = AssetBase & {
  customAssetId: string;
  purchaseDate?: string;
  purchasePrice?: number;
  purchasePriceCurrencyCode?: CurrencyCodeEnum;
  purchasedFromUrl: string;
  brand: string;
  model: string;
  serialNumber: string;
  notes: string;
  internalNotes: string;
  site: Site | null;
  status: AssetStatus;
  condition?: AssetCondition;
  imageUrl: string;
  assignedTo?: string;
  assignedToEmployee?: AssetAssignmentEmployee;
  device: AssetDeviceProperty | null;
  mdmStatusOverride?: AssetsMdmStatus;
  warrantyExpirationDate?: string;
  locationId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AssetWithEnrollmentStatus = Asset & {
  wasElectricAppInstalled?: boolean;
  wasMDMInstalled?: boolean;
};

// The camelCase version of the AssetDevice from Core Entity
// This represents the device property of an asset
export type AssetDeviceProperty = {
  id: string;
  osType: OSType;
  hardwareModel: string;
  lockStatus: LockStatus;
  lockPin?: string;
  mdmEnrolledAt?: string;
  mdmStatus?: AssetsMdmStatus;
  mdmProviderId?: string;
  electricAppInstalledAt?: string;
  reportingData?: AssetDeviceReportingData;
  createdAt: string;
  updatedAt: string;
};

export type Issue = {
  category?: string;
  actual?: boolean | number | string;
  expected?: boolean | number | string;
  extra?: {
    disabled?: boolean;
    reason?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export type EOLData = {
  status: EOLStatus;
  eolDate: string;
};

// The camelCase version of the AssetDeviceReportingData from Core Entity.
// This represents the data we get from device-reporting when we fetch an asset.
export type AssetDeviceReportingData = {
  hostname?: string;
  osVersion?: string;
  osName?: string;
  ramCapacity?: number;
  storageCapacity?: number;
  storageAvailable?: number;
  storageUsagePercent?: number;
  filevault?: boolean;
  filevaultStatus?: FilevaultStatus;
  filevaultEncryptionPercent?: number;
  bitlocker?: boolean;
  bitlockerStatus?: BitlockerStatus;
  bitlockerEncryptionPercent?: number;
  encryption?: boolean;
  encryptionPercent?: number;
  batteryHealth?: BatteryHealthStatus;
  firewall?: boolean;
  gatekeeper?: boolean;
  issues?: { [key: string]: Issue };
  editionId?: string;
  lastOsqueryReport?: string;
  storageInUse?: number;
  hasOsqueryIssue?: boolean;
  lastOsqueryEnrollment?: string;
  hasMdmIssue?: boolean;
  eolData?: EOLData; // This will be calculated in the frontend based on the osVersion
};

export type AssetIssue = {
  [DeviceIssueType.Encryption]?: AssetIssueDetails;
  [DeviceIssueType.Firewall]?: AssetIssueDetails;
  [DeviceIssueType.Gatekeeper]?: AssetIssueDetails;
  [DeviceIssueType.OS]?: AssetIssueDetails;
  [DeviceIssueType.Storage]?: AssetIssueDetails;
};

export type AssetIssueExtra = {
  eolDate: string;
  link: string;
};

export type AssetIssueDetails = {
  category: string;
  actual: string;
  expected: string;
  extra?: AssetIssueExtra | null;
};

// Typeguard for checking if an Asset is a DeviceAsset
export const isDeviceAsset = (asset: Asset): asset is DeviceAsset => {
  return !!asset.device;
};

/**
 * True for any asset an offboarding employee could physically hand back, i.e.
 * anything that isn't a service, warranty or shipping line item.
 */
export const isRetrievableAsset = (asset: Asset): boolean => {
  return !NON_PHYSICAL_PRODUCT_TYPES.includes(asset.productType.name);
};

/** True for desktop/laptop only. Use for desktop-specific features (e.g. Electric for Desktop). */
export const isDesktopOrLaptopAsset = (asset: Asset): boolean => {
  return (
    asset.productType.name === ProductTypeName.Laptop ||
    asset.productType.name === ProductTypeName.Desktop
  );
};

// This represents an Asset that is a device as indicated by the presence of the device property.
export type DeviceAsset = Asset & { device: NonNullable<Asset['device']> };

export type AssetAssignmentEmployee = {
  id: string;
  firstName: string;
  lastName: string;
  organizationPackageId?: string;
};

// `AssetAssignmentEmployee` plus PII (email, address) needed for shipment logistics.
// Only populated from the eligible-for-shipment endpoint — must not be merged into
// `AssetAssignmentEmployee` directly, since that would leak PII into general asset endpoints.
export type ShipmentEligibleAssetEmployee = AssetAssignmentEmployee & {
  email?: string;
  phoneNumber?: string;
  address?: Address;
};

// The camelCase version of ShipmentAsset from Core Entity, used by the
// eligible-for-shipment endpoint. Richer than `Asset`: the assigned employee
// carries PII (email/address) and the site includes its address.
export type ShipmentEligibleAsset = Omit<
  Asset,
  'assignedToEmployee' | 'site'
> & {
  assignedToEmployee?: ShipmentEligibleAssetEmployee;
  site: SiteWithAddress | null;
};

export type AssetLogDeviceIssue = {
  employee_id?: string;
  issue_type?: DeviceIssueType;
  device_id?: string;
};

export type AssetHistory<T = Record<string, unknown>> = {
  id: string;
  assetId: string;
  action: string;
  data: T;
  description: string;
  performedBy: string | undefined;
  performedByUser: string | undefined;
  performedAt: string;
  parentAssetLogId: string | undefined;
  createdAt: string;
  updatedAt: string | undefined;
};

export type AssetHistoryWithChildren<T = Record<string, unknown>> =
  AssetHistory<T> & {
    children?: AssetHistory<T>[];
  };

export type AssetListArgs = {
  organizationId: string;
  orderBy: string;
  sortOrder: string;
  searchTerm?: string;
  statuses?: AssetStatus[];
  productTypes?: ProductTypeName[];
  warrantyStatus?: WarrantyStatus[];
  hasAssignee?: boolean;
  isDevice?: boolean;
  assignedTo?: string;
  hasMdmEntitledAssignee?: boolean;
  mdmStatus?: AssetsMdmStatus[];
  includeMdmStatus?: boolean;
  healthStatus?: HealthStatus[];
  eolStatus?: OSEOLStatus[];
} & PaginationParams;

export type AssetListResponse = {
  total: number;
  assets: Asset[];
};

export type EligibleForShipmentAssetListArgs = {
  organizationId: string;
  searchTerm?: string;
} & PaginationParams;

// The camelCase version of the StorefrontAsset from Core Entity
export type StorefrontAsset = Asset & {
  isShippable: boolean;
  selfShipRequired: boolean;

  /** Future property (it will exist in the backend when we decide to show product recommendations for assets). It's already defined in the frontend, so the entire code can be more generic. */
  relatedProductsByCategory?: {
    categoryName: RelatedProductCategoryNameEnum;
    defaultProductId?: string;
    products?: Product[];
  }[];
};

export type AssetStorefrontListArgs = {
  organizationId: string;
  shoppingCountryCode: string;
  productTypes?: ProductTypeName[];
  shippableOnly?: boolean;
} & PaginationParams;

export type AssetStorefrontListResponse = {
  total: number;
  results: StorefrontAsset[];
  totalNonShippable: number;
};

export type AssetStorefrontDetailArgs = {
  organizationId: string;
  assetId: string;
  shoppingCountryCode: string;
};

export type AssetHistoryArgs = {
  organizationId: string;
  assetId: string;
} & PaginationParams;

type OrganizationAssetHistoryArgsParams = {
  organizationId: string;
  assetActions?: AssetActionEnum[];
  ignoreStatuses?: AssetStatus[];
  days?: number;
  ignoreDaysForUnresolvedIssues?: boolean;
};

export type OrganizationAssetHistoryArgs = OrganizationAssetHistoryArgsParams &
  PaginationParams;

export type AssetHistoryResponse = {
  total: number;
  assetHistory: AssetHistory[];
};

export type AssetHistoryWithChildrenResponse = {
  total: number;
  assetHistory: AssetHistoryWithChildren<AssetLogDeviceIssue>[];
};

export type AssetCreationArgs = {
  name: string;
  productTypeId: string;
  customAssetId: string | undefined;
  assignedTo: string | undefined;
  purchaseDate: Date | undefined;
  purchasePrice: number | undefined;
  purchasePriceCurrencyCode?: CurrencyCodeEnum;
  purchasedFromUrl: string | undefined;
  brand: string | undefined;
  model: string | undefined;
  serialNumber: string | undefined;
  notes: string | undefined;
  internalNotes: string | undefined;
  siteId: string | undefined;
  locationId?: string | undefined;
  status: AssetStatus | undefined;
  condition: AssetCondition | undefined;
  imageUrl: string | undefined;
  warrantyExpirationDate: Date | undefined;
};

export type AssetsCreationArgs = {
  organizationId: string;
  assets: AssetCreationArgs[];
};

export type AssetUpdateRequestDto = Omit<
  AssetUpdateRequest,
  'assigned_to' | 'site_id' | 'purchase_price' | 'warranty_expiration_date'
> & {
  assigned_to: AssetUpdateRequest['assigned_to'] | null;
  site_id: AssetUpdateRequest['site_id'] | null;
  purchase_price: AssetUpdateRequest['purchase_price'] | null;
  warranty_expiration_date:
    | AssetUpdateRequest['warranty_expiration_date']
    | null;
};

export type AssetUpdateArgs = Partial<
  Omit<AssetCreationArgs, 'assignedTo' | 'siteId' | 'purchasePrice'>
> & {
  organizationId: string;
  assetId: string;
  assignedTo?: AssetCreationArgs['assignedTo'] | null;
  siteId?: AssetCreationArgs['siteId'] | null;
  purchasePrice?: AssetCreationArgs['purchasePrice'] | null;
  warrantyExpirationDate?: AssetCreationArgs['warrantyExpirationDate'] | null;
};

export type Site = {
  id: string;
  name: string;
  phone?: string;
  address?: Address;
  organizationId: string | null;
  createdAt?: string;
  archivedAt: string | null;
  isArchivable: boolean;
};

export type SiteWithAddress = Site & {
  address: Address;
};

export type SiteCreationArgs = {
  name: string;
  phone?: string;
  address: Address;
  organizationId: string;
};

export type SiteListArgs = {
  organizationId?: string;
  employeeSites?: boolean;
} & PaginationParams;

export type CSVImportAsset = {
  name: string;
  assetID: string;
  productType: ProductTypeOption | undefined;
  assignedTo: { name: string; value: string; label: string } | undefined;
  purchaseDate: string | undefined;
  purchasePrice: string | undefined;
  purchasePriceCurrencyCode?: CurrencyCodeEnum;
  purchasedFrom: string | undefined;
  brand: string | undefined;
  model: string | undefined;
  serialNumber: string | undefined;
  notes: string | undefined;
  site: SiteOption | undefined;
  status: AssetStatusOption | undefined;
  condition: AssetConditionOption | undefined;
  warrantyExpirationDate: string | undefined;
};

export type IgnoreAssetLogArgs = {
  organizationId: string;
  assetLogId: string;
};

export type Location = {
  id: string;
  name: string;
  siteId: string;
};

export type LocationCreationArgs = {
  siteId: string;
  name: string;
};

export type AddFileToAssetArgs = {
  organizationId: string;
  assetId: string;
  formData: FormData;
};

export type PresignedUploadUrlInfo = {
  filename: string;
  presignedUrl: string;
  fileId: string;
  s3Key: string;
  expiresIn: number;
};

export type FileUploadError = {
  filename: string;
  error: string;
};

export type PresignedUploadUrlResponse = {
  message: string;
  presignedUrls: PresignedUploadUrlInfo[];
  errors: FileUploadError[];
};

export type PresignedUploadUrlRawResponse = {
  message: string;
  presigned_urls: Array<{
    filename: string;
    presigned_url: string;
    file_id: string;
    s3_key: string;
    expires_in: number;
  }>;
  errors: Array<{
    filename: string;
    error: string;
  }>;
};

export type GeneratePresignedUploadUrlsArgs = {
  organizationId: string;
  assetId: string;
  fileSamples: File[];
  fileNames: string[];
  fileSizes: number[];
  contentTypes: string[];
};

export type AssetFileWithURL = {
  fileId: string;
  url: string;
  fileName: string;
  assetId: string;
  fileSizeBytes: number;
  fileType: string;
  uploadStatus: string;
  createdAt?: string;
  updatedAt?: string;
};

export type WarehouseMetrics = {
  assetCounts: AssetCountTableRow[];
};

export type MonthKey = 'previousMonth' | 'currentMonth';

export type AssetCountTableRow = {
  month: MonthKey;
  count: number;
};

export type AssetEventsHistoryArgs = {
  organizationId: string;
  assetId: string;
  pageCursorDate?: string;
  actionTypes?: ActionType[];
  categories?: AssetEventCategory[];
  sortOrder?: SortOrder;
};

export type AssetEvent = {
  action: string;
  date: string;
  actionType: ActionType;
  category: AssetEventCategory;
  source?: AssetEventSource;
  performedBy?: string;
  extraInfo?: Record<string, unknown>;
};

export type AssetEventsHistoryResponse = {
  assetEvents: AssetEvent[];
  lastDate: string;
};

export type BulkAssetsArgs = {
  assetIds: string[];
  organizationId: string;
};

export type BulkAssetsResponse = {
  assetsMapping: Record<string, Asset>;
};
